import { ReactElement } from "react";
import Chart from "./chart";
import { getTicks, useChartDimensions, useUniqueId } from "./chartUtils";
import { IAxisBand, IAxisLinear, IPoint, IRadarChartConfig, SeverityEnum } from "./types";
import * as d3 from "d3";
import { getPalette } from "../../configuration/configuration";
import { useTheme } from "@mui/material";
import Legend from "./legend";
import HtmlTooltip from "./tooltip";
import { formatNumber } from "../../system/utils";

export interface IProps {
  radarConfig: IRadarChartConfig;
  selectedItem: number | string | null;
  size?: number;
}

const getCoordinatesForAngleAndRadius = (angle: number, radius: number) => [
  Math.cos(angle - Math.PI / 2) * radius,
  Math.sin(angle - Math.PI / 2) * radius,
];

const getSeverityColor = (severity: SeverityEnum): string => {
  switch (severity) {
    case SeverityEnum.error:
      return  "red";
    case SeverityEnum.warning:
      return "orange";
    case SeverityEnum.info:
      return "slategray";
    default:
      return "#bdc3c7";
  }
};


const RadarChart = ({ radarConfig, selectedItem, size }: IProps): ReactElement => {
  // const width = radarConfig.width ?? 100;
  // const height = radarConfig.height ?? width;
  const theme = useTheme();
  const shapeRadius = size ?? 5;
  const shapeDiameter = shapeRadius * 2;
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: radarConfig.height ?? undefined,
    width: radarConfig.width ?? undefined,
    radius: Math.min(radarConfig.width ?? Number.MAX_VALUE, radarConfig.height ?? Number.MAX_VALUE) / 2.0,
    marginBottom: radarConfig.title ? 40 : 10,
    marginLeft: 10,
    marginRight: 10,
    marginTop: radarConfig.showLegend ? 20 : 10
  });

  const radarID = useUniqueId("radar-chart");
  // const seriesAccessor = (d: ISeries) => d.name;

  const getCoordinatesForAngle = (angle: number, offset = 1) => [
    Math.cos(angle - Math.PI / 2) * (dimensions.boundedRadius ?? 0) * offset,
    Math.sin(angle - Math.PI / 2) * (dimensions.boundedRadius ?? 0) * offset,
  ];

  let isBand = "categories" in radarConfig.xAxis;

  const xTicks = isBand
    ? (radarConfig.xAxis as IAxisBand).categories
    : [(radarConfig.xAxis as IAxisLinear).minimum, (radarConfig.xAxis as IAxisLinear).maximum];

  const angleScale = isBand
    ? d3.scaleBand()
      .domain(xTicks as string[])
      .range([0, Math.PI * 2])
    : d3.scaleLinear()
      .domain(xTicks as number[])
      .range([0, Math.PI * 2])
      .nice();

  isBand = "categories" in radarConfig.yAxis;

  const radiusScale = isBand
    ? d3.scaleBand()
      .domain((radarConfig.xAxis as IAxisBand).categories)
      .range([0, Math.PI * 2])
    : d3.scaleLinear()
      .domain([(radarConfig.yAxis as IAxisLinear).minimum, (radarConfig.yAxis as IAxisLinear).maximum])
      .range([0, dimensions.boundedRadius ?? 0])
      .nice();

  const ticks = isBand
    ? (radarConfig.xAxis as IAxisBand).categories
    : getTicks(radiusScale.domain() as number[], (radarConfig.yAxis as IAxisLinear).ticksCount);

  const xAxisBand = radarConfig.xAxis as IAxisBand;

  return (
    <div ref={ref} >
      <Chart dimensions={dimensions}>
        {radarConfig.showLegend &&
          <Legend series={radarConfig.series} chartID={radarID} width={dimensions.boundedWidth}/>}

        <g transform={`translate(${dimensions.boundedWidth / 2.0}, ${(dimensions.radius ?? 0) * 1.1})`}>
          {radarConfig.title &&
            <text x={0} y={dimensions.radius ?? 0} className="tick-label-title" fill={theme.palette.text.primary}>
              {radarConfig.title}
            </text>}

          <g>
            {xTicks.length <= 24 && xTicks.map((category, index) => {

              const angle = angleScale(category as any) ?? 0;
              const [x, y] = getCoordinatesForAngle(angle);
              const [labelX, labelY] = getCoordinatesForAngle(angle, 1.10);
              const textAnchor = Math.abs(labelX) < 5 ? "middle" : labelX > 0 ? "start" : "end";

              let title = category;
              if (xAxisBand.formatTick) {
                title = xAxisBand.formatTick(category, 0);
              }
              // {hasUnit && <small style={{ fontSize: "0.5em" }}>{` [${variable0.unit}]`}</small>}

              return (
                <g key={`${radarID}-line-${index}`}>
                  <line className="Axis__line" x2={x} y2={y}/>
                  <text x={labelX} y={labelY} textAnchor={textAnchor} className="tick-label">
                    {title}
                    {/*<tspan  style={{ fontSize: "0.7em" }}>[F]</tspan >*/}
                  </text>
                </g>
              );
            })
            }
          </g>

          <g>
            {ticks.map((t, index) => {
              const r = radiusScale(t as any) ?? 0;
              const text = `${radarConfig.yAxis.formatTick ? radarConfig.yAxis.formatTick(t, index, undefined) : t}`;

              return (
                <g key={`${radarID}-circle-${index}`}>
                  <circle r={r} className="Axis__line" fill="none"/>
                  {!radarConfig.yAxis.hiddenTickText && index > 0 &&
                    <rect x={-text.length * 2.5} y={-r - 3} width={text.length * 5} height={8}
                      fill={theme.palette.background.paper}/>}
                  {!radarConfig.yAxis.hiddenTickText && index > 0 &&
                    <text x={0} y={-r + 2} textAnchor="middle"
                      className="tick-label">{text}
                    </text>
                  }
                </g>
              );
            })}
          </g>

          {radarConfig.series.map((series, indexS) => {
            const points = (series.value as IPoint[]).map((point) => {
              const angle = angleScale(point.x as any);

              if (angle === null || angle === undefined) {
                return "";
              }

              const value = series.converter ? series.converter(point as any) : point.y;
              const radius = radiusScale(value as any) ?? 0;
              const [x, y] = getCoordinatesForAngleAndRadius(angle, radius);

              return `${x} ${y}`;
            });

            const path = points.join(" ");

            return (
              <polygon key={`${radarID}-series-path-${indexS}`} points={path} fill={series.color}
                fillOpacity={0.2}/>
            );
          })}

          {radarConfig.series.map((series, indexS) => {
            return (
              <g key={`${radarID}-series-${indexS}`}>

                {(series.value as IPoint[]).map((point, index) => {
                  const angle = angleScale(point.x as any);
                  if (angle === null || angle === undefined) {
                    return "";
                  }

                  const value = series.converter ? series.converter(point as any) : point.y;
                  const radius = radiusScale(value as any) ?? 0;

                  const [x, y] = getCoordinatesForAngleAndRadius(angle, radius);

                  let color = point.color ?? series.color;
                  let range = null;
                  if (series.ranges) {
                    range = series.ranges.find(r => point.y >= r.minimum && point.y <= r.maximum);
                    if (range) {
                      color = range.color;
                    }
                  }

                  return (
                    <HtmlTooltip key={`${radarID}-circle-${index}`} title={
                      <div className="display-flex-column">
                        <div className="display-flex-column align-items-center">
                          <strong>{xAxisBand.formatTick ? xAxisBand.formatTick(point.x, 0) : point.x}</strong>
                        </div>
                        <div className="display-flex-row align-items-center">
                          <div style={{
                            background: color,
                            width: 10,
                            height: 10,
                            marginRight: 10
                          }}/>
                          <strong className="flex-grow-1">{series.name}</strong>
                          <strong className="flex-grow-0">
                            {radarConfig.yAxis.formatTick ? radarConfig.yAxis.formatTick(point.y, 0) : formatNumber(point.y, 1)}
                            {series.unit && <sub>{`[${series.unit}]`}</sub>}
                          </strong>
                        </div>
                        {range &&
                          <strong style={{ marginLeft: 20 }}>{`Range: [ ${
                            radarConfig.yAxis.formatTick ? radarConfig.yAxis.formatTick(range.minimum, 0) : formatNumber(range.minimum, 1)}, ${
                            radarConfig.yAxis.formatTick ? radarConfig.yAxis.formatTick(range.maximum, 0) : formatNumber(range.maximum, 1)
                          } ]`}
                          </strong>
                        }
                      </div>
                    }>
                      <>
                        {(!point.status || point.status === "NONE" || point.status === "OK") &&
                          <circle cx={x} cy={y} r={shapeRadius}
                            stroke={getSeverityColor(point.severity ?? SeverityEnum.none)}
                            strokeWidth={.5}
                            fill={selectedItem === point.x ? getPalette().statusColor.selected.background : color}/>
                        }
                        {point.status === "UP" &&
                          <g transform={`translate(${x - shapeRadius}, ${y - shapeRadius})`}>
                            <polygon
                              fill={selectedItem === point.x ? getPalette().statusColor.selected.background : color}
                              stroke={getSeverityColor(point.severity ?? SeverityEnum.none)} strokeWidth={point.severity ? 2 : .5}
                              points={`0 ${shapeDiameter} ${shapeDiameter} ${shapeDiameter} ${shapeRadius} 0 0 ${shapeDiameter}`}/>
                          </g>
                        }
                        {point.status === "DOWN" &&
                          <g transform={`translate(${x - shapeRadius}, ${y - shapeRadius})`}>
                            <polygon
                              fill={selectedItem === point.x ? getPalette().statusColor.selected.background : color}
                              stroke={getSeverityColor(point.severity ?? SeverityEnum.none)} strokeWidth={point.severity ? 2 : .5}
                              points={`0 0 ${shapeRadius} ${shapeDiameter} ${shapeDiameter} 0 0 0`}/>
                          </g>
                        }
                        {point.status === "BAD" &&
                          <g transform={`translate(${x - shapeRadius}, ${y - shapeRadius})`}>
                            <polygon
                              fill={selectedItem === point.x ? getPalette().statusColor.selected.background : color}
                              stroke={getSeverityColor(point.severity ?? SeverityEnum.none)} strokeWidth={point.severity ? 2 : .5}
                              points={`${shapeRadius} 0 0 ${shapeRadius} ${shapeRadius} ${shapeDiameter} ${shapeDiameter} ${shapeRadius} ${shapeRadius} 0`}/>
                          </g>
                        }
                      </>
                    </HtmlTooltip>
                  );
                })}
              </g>
            );
          })
          }
        </g>
      </Chart>
    </div>
  );
};

export default RadarChart;
